<script setup>
import { ref } from 'vue'

const props = defineProps({
  requirementTitle: { type: String, required: true },
  riskScoreBefore: { type: String, default: '--' },
  riskScoreAfter: { type: String, required: true },
  riskAmountBefore: { type: String, default: '--' },
  riskAmountAfter: { type: String, required: true },
  assessmentUpdateText: { type: String, default: '' },
  employerName: { type: String, required: true }
})

const emit = defineEmits(['back-to-list', 'go-to-report', 'go-to-assessment', 'share-update'])

const showNotesAccordion = ref(false)
const notes = ref('')

function handleBackToList() {
  emit('back-to-list')
}

function handleGoToReport() {
  emit('go-to-report')
}

function handleGoToAssessment() {
  emit('go-to-assessment')
}

function handleShareUpdate() {
  emit('share-update')
}
</script>

<template>
  <div>
    <!-- Success Message -->
    <button id="backtoListButton" type="button" class="btn btn-link p-0 mt-1 mb-3 text-14 text-primary text-decoration-none" 
      @click="handleBackToList">
      <strong><i class="bi bi-chevron-left me-1"></i> Back to All {{ employerName }} Action Items</strong>
    </button>
    <div class="success-section text-center mb-3 pt-4.5">
      <div class="success-checkmark mb-2">
        <img id="successCircleImage" src="@/assets/images/success_circle.svg" alt="Success" style="width: 112px; height: 45px;" />
      </div>
      <h5 class="fw-bold header-20 mb-2">
        <strong id="requirementTitle">{{ requirementTitle }} requirement has been marked complete!</strong>
      </h5>
      <p id="riskScoreAndAmountUpdatedText" class="text-14 text-muted mb-0">
        The risk score and amount have been updated. This change is now reflected on the BCS Report 
        page and PDF, employer overview and on the employer's Assessment page.
      </p>
    </div>

    <!-- Update Details -->
    <div class="mb-4">
      <h6 id="updateDetailsTitle" class="pt-0.5 header-20 mb-3">
        <strong>Update Details</strong> 
      </h6>

      <!-- Reduced Exposure -->
      <div class="mb-2">
        <p id="reducedExposureTitle" class="pt-1 text-16 mb-2">
          <strong>Reduced Exposure</strong></p>
        <p id="reducedExposureRiskScore" class="text-16 mb-1">
          Improved Risk Score: <strong id="reducedExposureRiskScoreValueAfter">{{ riskScoreAfter }}</strong>
          <span id="reducedExposureRiskScoreValueBefore" v-if="riskScoreBefore" class="text-muted"> (formerly {{ riskScoreBefore }})</span>
        </p>
        <p id="reducedExposureRiskAmount" class="text-16 mb-2">
          Decreased Risk Amount: <strong id="reducedExposureRiskAmountValueAfter">{{ riskAmountAfter }}</strong>
          <span id="reducedExposureRiskAmountValueBefore" v-if="riskAmountBefore" class="text-muted"> (formerly {{ riskAmountBefore }})</span>
        </p>
        <button
          id="goToReportButton"
          type="button"
          class="btn btn-link text-primary text-decoration-none p-0 text-16 fw-bold"
          @click="handleGoToReport"
        >
          Go to BCS Pro Report
        </button>
      </div>
      
      <hr class="mx-auto" />

      <!-- Updated Assessment -->
      <div class="mb-4">
        <p class="fw-bold text-16 mb-2">Updated Assessment</p>
        <p class="text-16 mb-2">
          {{ assessmentUpdateText || 'Questions on the Assessment page have been updated to reflect the completed requirement.' }}
        </p>
        <button
          id="goToAssessmentButton"
          type="button"
          class="btn btn-link text-primary text-decoration-none p-0 text-16 fw-bold"
          @click="handleGoToAssessment"
        >
          Go to Assessment Page
        </button>
      </div>
      
      <hr class="mx-auto my-1 mt-n2" />

      <!-- Add Notes Accordion -->
      <div class="accordion mb-4" id="confirmationNotesAccordion">
        <div class="accordion-item border-0">
          <h2 class="accordion-header" id="confirmationNotesHeading">
            <button
              id="addNotesButton"
              class="accordion-button text-16 bg-white border-0 shadow-none px-0"
              :class="{ collapsed: !showNotesAccordion }"
              type="button"
              @click="showNotesAccordion = !showNotesAccordion"
              :aria-expanded="showNotesAccordion"
              aria-controls="confirmationNotesCollapse"
            >
              <strong class="text-primary">Add Notes About This Update (optional)</strong>
            </button>
          </h2>
          <div
            id="confirmationNotesCollapse"
            class="accordion-collapse collapse"
            :class="{ show: showNotesAccordion }"
            aria-labelledby="confirmationNotesHeading"
          >
            <div class="accordion-body px-0 pt-3">
              <label class="form-label text-16 fw-bold mb-2">Notes</label>
              <textarea
                class="form-control text-16"
                rows="5"
                v-model="notes"
                placeholder="Add notes here..."
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-between button-container">
      <button
        id="backToListButton"
        type="button"
        class="btn btn-outline-primary text-16"
        @click="handleBackToList"
      >
        <i class="bi bi-chevron-left me-1"></i>
      Back to Full List
      </button>
      <button
        id="shareUpdateButton"
        type="button"
        class="btn btn-primary text-16"
        @click="handleShareUpdate"
      >
        Share This Update
        <i class="bi bi-send-fill text-white ms-1"></i>
      </button>
    </div>
   
  </div>
</template>

<style scoped lang="scss">
.success-section {
  background-color: #E3F1EB;
  margin-left: -2rem;  /* Negative margin to counteract offcanvas-body padding */
  margin-right: -2rem;
  padding-left: 2rem;  /* Add padding back for content */
  padding-right: 2rem;
  padding-bottom: 1.5rem; /* Add bottom padding */
}

.success-checkmark {
  animation: scaleIn 0.3s ease-out;
}

.button-container {
  background-color: var(--bs-light);
  padding: 16px;
  position: fixed;
  bottom: 0;
  right: 0;
  width: 510px;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
